import { trpc } from "@/lib/trpc";
import { Image, Loader2, X, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface PhotoGalleryProps {
  dailyLogId: number;
  canDelete?: boolean;
}

export default function PhotoGallery({ dailyLogId, canDelete = false }: PhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [instrumentFilter, setInstrumentFilter] = useState<string>("all");
  
  const { data: attachments, isLoading, refetch } = trpc.attachments.list.useQuery({ dailyLogId });
  const deleteAttachmentMutation = trpc.attachments.delete.useMutation();

  const handleDelete = async (id: number) => {
    try {
      await deleteAttachmentMutation.mutateAsync({ id });
      toast.success("Photo deleted");
      refetch();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete photo");
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "meter":
        return "Meter";
      case "chlorine_tank":
        return "Chlorine";
      default:
        return "Other";
    }
  };

  const getFieldLabel = (fieldContext: string | null) => {
    if (!fieldContext) return null;
    switch (fieldContext) {
      case "meterReadEnd":
        return "Meter Reading";
      case "measuredInchesCl2":
        return "Chlorine Gauge";
      case "residual":
        return "Tower Residual";
      case "psi":
        return "Pressure";
      case "hours":
        return "Hour Meter";
      default:
        return fieldContext;
    }
  };

  // Filter attachments by instrument
  const filteredAttachments = useMemo(() => {
    if (!attachments) return [];
    if (instrumentFilter === "all") return attachments;
    return attachments.filter(a => a.instrumentId === instrumentFilter);
  }, [attachments, instrumentFilter]);

  // Get unique instruments for filter
  const instruments = useMemo(() => {
    if (!attachments) return [];
    const uniqueInstruments = new Set(attachments.map(a => a.instrumentId).filter(Boolean));
    return Array.from(uniqueInstruments).map(id => ({
      id,
      name: attachments.find(a => a.instrumentId === id)?.instrumentName || id,
    }));
  }, [attachments]);

  // Group attachments by field context
  const groupedAttachments = filteredAttachments?.reduce((acc, attachment) => {
    const key = attachment.fieldContext || "general";
    if (!acc[key]) acc[key] = [];
    acc[key].push(attachment);
    return acc;
  }, {} as Record<string, typeof filteredAttachments>);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No photos attached</p>
      </div>
    );
  }

  return (
    <>
      {/* Instrument filter */}
      {instruments.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={instrumentFilter} onValueChange={setInstrumentFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by instrument" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Photos</SelectItem>
              {instruments.map(instrument => (
                <SelectItem key={instrument.id!} value={instrument.id!}>
                  {instrument.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-4">
        {Object.entries(groupedAttachments || {}).map(([context, contextAttachments]) => (
          <div key={context}>
            {context !== "general" && (
              <h4 className="text-sm font-medium mb-2">
                {getFieldLabel(context)} Photos
              </h4>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {contextAttachments.map((attachment) => (
          <div key={attachment.id} className="relative group">
            <div
              className="aspect-square rounded-lg border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => setSelectedPhoto(`/api/attachments/${attachment.id}`)}
            >
              <img
                src={`/api/attachments/${attachment.id}`}
                alt={attachment.fileName || "Attachment"}
                className="w-full h-full object-cover"
                onError={(e) => {
                  console.warn(`Proxy thumbnail failed for ${attachment.id}: /api/attachments/${attachment.id}`);
                  (e.target as HTMLImageElement).src = attachment.fileUrl || '';
                  (e.target as HTMLImageElement).style.backgroundColor = '#f3f4f6';
                }}
              />
            </div>
            <div className="absolute top-1 left-1 flex gap-1">
              <Badge variant="secondary" className="text-xs">
                {getTypeLabel(attachment.attachmentType || "other")}
              </Badge>
              {attachment.fieldContext && (
                <Badge variant="outline" className="text-xs">
                  {getFieldLabel(attachment.fieldContext)}
                </Badge>
              )}
            </div>
            {canDelete && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(attachment.id);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            {(attachment.displayFileName || attachment.fileName) && (
              <p className="text-xs text-muted-foreground truncate mt-1" title={attachment.displayFileName || attachment.fileName || undefined}>
                {attachment.displayFileName || attachment.fileName}
              </p>
            )}
            {attachment.operatorInitial && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {attachment.operatorInitial}
              </p>
            )}
          </div>
        ))}
            </div>
          </div>
        ))}
      </div>

      {/* Full-size photo dialog */}
      <Dialog open={selectedPhoto !== null} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Photo Preview</DialogTitle>
          </DialogHeader>
          {selectedPhoto && (
            <div className="flex items-center justify-center">
              <img
                src={selectedPhoto}
                alt="Full size"
                className="max-w-full max-h-[70vh] object-contain"
                onError={(e) => console.warn('Full-size image failed to load:', selectedPhoto, e)}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
